// apps/web/src/components/message-input.tsx
import { useState, useRef, type KeyboardEvent } from 'react'
import { Button } from '@viecxanh/chat-ui/button'
import { Textarea } from '@viecxanh/chat-ui/textarea'
import { useMessages } from '../hooks/use-messages'
import type { QuickReply } from '@viecxanh/chat-shared'

interface MessageInputProps {
  conversationId: string
  quickReplies?: QuickReply[]
}

export function MessageInput({ conversationId, quickReplies = [] }: MessageInputProps) {
  const [content, setContent] = useState('')
  const [sending, setSending] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const { sendMessage } = useMessages(conversationId)

  const handleSend = async () => {
    const trimmed = content.trim()
    if (!trimmed || sending) return
    setSending(true)
    try {
      await sendMessage({ content: trimmed, type: 'text' })
      setContent('')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      if (!res.ok) throw new Error('Upload failed')
      const { url, name, size } = await res.json() as { url: string; name: string; size: number }
      const type = file.type.startsWith('image/') ? 'image' : 'file'
      await sendMessage({ content: name, type, attachments: [{ url, name, size, type }] })
    } catch (err) {
      console.error('File upload error:', err)
    }
    e.target.value = ''
  }

  // Quick reply: typing "/" shows dropdown
  const filteredReplies = content.startsWith('/')
    ? quickReplies.filter((qr) =>
        qr.shortcut.startsWith(content) || qr.title.toLowerCase().includes(content.slice(1).toLowerCase()),
      )
    : []

  return (
    <div className="border-t bg-white p-3">
      {filteredReplies.length > 0 && (
        <div className="mb-2 rounded-md border bg-white shadow-sm">
          {filteredReplies.slice(0, 6).map((qr) => (
            <button
              key={qr._id}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => setContent(qr.content)}
            >
              <span className="font-mono text-xs text-slate-500">{qr.shortcut}</span>
              <span className="flex-1 truncate">{qr.title}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => void handleFileChange(e)} />

        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={() => fileRef.current?.click()}
          type="button"
          aria-label="Đính kèm file"
        >
          📎
        </Button>

        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Nhập tin nhắn... (Enter để gửi, Shift+Enter xuống dòng)"
          className="min-h-[40px] max-h-32 resize-none"
          rows={1}
        />

        <Button
          onClick={() => void handleSend()}
          disabled={!content.trim() || sending}
          className="flex-shrink-0 bg-brand hover:bg-brand-dark"
        >
          Gửi
        </Button>
      </div>
    </div>
  )
}
